import type { InvoicePart, InvoiceType } from '../types/invoice';
import type { LocationDefaultTaxRate } from '../hooks/useGetSettings';
import { isCreditCardMiscFeeLine } from './invoicePaySettlement';

type PartWithTotal = { totalBuyPrice?: number; buyPrice?: number };

function sumPartLineTotals(items: PartWithTotal[] | undefined): number {
    return (items ?? []).reduce((sum, item) => {
        const amount = item.totalBuyPrice ?? item.buyPrice;
        return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);
}

/** Above-line-items subtotal from persisted invoice rows (matches detail-page tab totals). */
export function computeAboveLineItemsSubtotalFromInvoice(invoice: {
    lineItems?: PartWithTotal[];
    freightItems?: PartWithTotal[];
    fuelItems?: PartWithTotal[];
    restockingFeeItems?: InvoicePart[];
    paySettlementType?: string;
}): number {
    const skipCc = invoice.paySettlementType === 'charge';
    const restocking = (invoice.restockingFeeItems ?? []).filter(
        (item) => !(skipCc && isCreditCardMiscFeeLine(item)),
    );
    return (
        sumPartLineTotals(invoice.lineItems) +
        sumPartLineTotals(invoice.freightItems) +
        sumPartLineTotals(invoice.fuelItems) +
        sumPartLineTotals(restocking)
    );
}

/**
 * Org-configured default tax rate (0..1 decimal) for the invoice's shop location and type.
 * Returns null when no entry matches, no rate is set, or the toggle for the type is off.
 * `return_slip` invoices never receive a default tax.
 */
export function resolveLocationDefaultTaxRate(args: {
    locationDefaultTaxRates: LocationDefaultTaxRate[] | undefined;
    locationId: string | undefined;
    invoiceType: InvoiceType;
}): number | null {
    const entries = args.locationDefaultTaxRates;
    if (!entries?.length || !args.locationId?.trim()) return null;

    const locationIdStr = args.locationId.trim();
    const entry = entries.find((candidate) => candidate.locationId === locationIdStr);
    if (!entry || entry.taxRate == null || !Number.isFinite(entry.taxRate)) return null;

    const enabledForType =
        args.invoiceType === 'received'
            ? entry.applyToReceived
            : args.invoiceType === 'returned'
              ? entry.applyToCredited
              : args.invoiceType === 'sublet'
                ? entry.applyToSublet
                : false;
    return enabledForType === true ? entry.taxRate : null;
}

/**
 * Default tax computed off the above-line-items subtotal, rounded to cents. The sign follows
 * the subtotal: positive for received/sublet, negative for credited invoices.
 */
export function computeLocationDefaultTax(taxRate: number, aboveLineItemsSubtotal: number): number {
    return Math.round(taxRate * aboveLineItemsSubtotal * 100) / 100;
}

const toCents = (n: number): number => Math.round(n * 100);

export interface TaxPennyGapInput {
    /** Sum of line items used in the grand-total identity (ignored tab excluded). */
    aboveLineItemsSubtotal: number;
    /** GST/HST, or the single sales-tax field. This is the amount that may move by one cent. */
    taxesTotal: number;
    /** PST. Included in the total check and left unchanged. */
    pstTaxTotal: number;
    discountTotal: number;
    /** Header freight included in the grand total. Zero when freight is already a line item. */
    headerFreightForTotal: number;
    extractedTotal: number;
}

/**
 * When GST/HST, PST, or their sum leaves subtotal + tax + discount + header freight exactly one
 * cent away from the extracted grand total, return the GST/HST amount that makes that identity hold.
 * Discount and PST stay as they are. No tax, or any larger gap, keeps the current tax.
 */
export function adjustTaxForOneCentGrandTotalGap(input: TaxPennyGapInput): number {
    const taxesTotal = Number.isFinite(input.taxesTotal) ? input.taxesTotal : 0;
    if (!Number.isFinite(input.extractedTotal)) return taxesTotal;

    const taxCents = toCents(taxesTotal) + toCents(input.pstTaxTotal);
    if (taxCents === 0) return taxesTotal;

    const expectedCents =
        toCents(input.aboveLineItemsSubtotal) +
        taxCents +
        toCents(input.discountTotal) +
        toCents(input.headerFreightForTotal);
    const deltaCents = toCents(input.extractedTotal) - expectedCents;
    if (Math.abs(deltaCents) !== 1) return taxesTotal;

    return (
        toCents(input.extractedTotal) -
        toCents(input.aboveLineItemsSubtotal) -
        toCents(input.pstTaxTotal) -
        toCents(input.discountTotal) -
        toCents(input.headerFreightForTotal)
    ) / 100;
}

/**
 * Location-default tax shown on the invoice. Canadian shops move GST/HST by one cent when that
 * is all that separates the lines from the extracted grand total.
 */
export function locationDefaultTaxForGrandTotal(args: {
    taxRate: number;
    aboveLineItemsSubtotal: number;
    isCanadian: boolean;
    pstTaxTotal: number;
    discountTotal: number;
    headerFreightForTotal: number;
    extractedTotal: number;
}): number {
    const base = computeLocationDefaultTax(args.taxRate, args.aboveLineItemsSubtotal);
    if (!args.isCanadian) return base;
    return adjustTaxForOneCentGrandTotalGap({
        aboveLineItemsSubtotal: args.aboveLineItemsSubtotal,
        taxesTotal: base,
        pstTaxTotal: args.pstTaxTotal,
        discountTotal: args.discountTotal,
        headerFreightForTotal: args.headerFreightForTotal,
        extractedTotal: args.extractedTotal,
    });
}

export const LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE = 0.005;
